/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { createObservableDataAction, IAction, IActionContext, IActionInput, ICreateActionContext } from '@msdyn365-commerce/core';
import { AsyncResult, AttributeDataType, ChannelInventoryConfiguration, ProductRefinerSource, ProductRefinerValue, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import { getInventoryConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';

import { BaseCollectionInput, createBaseCollectionInput } from './base-collection-action';
import { getProductRefinerHierarchy } from './get-product-refiner-hierarchy';
import { getInventorySortableRefinerValue, ProductListInventoryFilteringOptions } from '.';
import { addOrUpdateZeroPriceRefiner, getAlcoholRefiners, getAllAttributeRefiners, shouldFilterAlcohol } from './refiner-utils';
import { IDlvrySearchResultContainerConfig } from '../dlvry-search-result-container.props.autogenerated';

/**
 * Default Category/Product Id Values.
 */
enum DefaultValues {
    defaultCategoryIdValue = 0,
    defaultProductIdValue = 0
}

/**
 * Refiners-by-Collection Input action.
 */
export class RefinersByCollectionInput extends BaseCollectionInput implements IActionInput {
    public getCacheObjectType = () => 'ProductRefiner';

    public dataCacheType = () => {
        return 'none';
        // @ts-ignore
        if (
            this.pageType !== 'Category' ||
            (this.refiners && this.refiners.length > 0) ||
            (this.queryResultSettings &&
                this.queryResultSettings.Sorting &&
                this.queryResultSettings.Sorting!.Columns &&
                this.queryResultSettings.Sorting!.Columns!.length > 0)
        ) {
            return 'request';
        }
        return 'application';
    };
}

/**
 * Create input method which creates an ActionInput for fetching list page refiners.
 * @param args
 */
const createInput = (args: ICreateActionContext<IDlvrySearchResultContainerConfig>): IActionInput => {
    const input = createBaseCollectionInput(args, RefinersByCollectionInput, args.config?.productScopeAttributeRecordId ? 'Category' : undefined);
    input.productScopeAttributeRecordId = args.config?.productScopeAttributeRecordId;
    input.productScopeAttributeValue = args.config?.productScopeAttributeValue;
    return input;
};

/**
 * Action method which fetches refiners for the given list page.
 * @param input
 * @param context
 */
export async function getRefinersForCollectionDataAction(input: RefinersByCollectionInput, context: IActionContext): Promise<IProductRefinerHierarchy[]> {
    let searchProductId;
    // Decouple refiners from input.refiners to avoid input.refiners being modified below
    let refiners: ProductRefinerValue[] = [...input.refiners];

    let channelInventoryConfigurationPromise: AsyncResult<ChannelInventoryConfiguration>;
    if (input.channelInventoryConfiguration) {
        channelInventoryConfigurationPromise = AsyncResult.resolve(input.channelInventoryConfiguration);
    } else {
        channelInventoryConfigurationPromise = getInventoryConfigurationAsync({ callerContext: context });
    }
    const channelInventoryConfiguration = await channelInventoryConfigurationPromise;

    // Replace InventoryProductAttribute with boolean refiner.
    // Because the value of InventoryProductAttribute is different from language to language since it's string type.
    // The cached old inventory refiner value may be hit when customer switch language which will lead to "no products found" issue.
    const inventoryRefiner = refiners.find(
        refiner => refiner.RefinerRecordId === channelInventoryConfiguration?.InventoryProductAttributeRecordId
    );
    if (inventoryRefiner) {
        refiners = refiners.filter(refiner => refiner.RefinerRecordId !== channelInventoryConfiguration?.InventoryProductAttributeRecordId);

        const isInStockRefiner =
            inventoryRefiner.LeftValueBoundString !== channelInventoryConfiguration?.InventoryOutOfStockAttributeValueText &&
            inventoryRefiner.RightValueBoundString !== channelInventoryConfiguration?.InventoryOutOfStockAttributeValueText;
        const inventoryRefinerValue = getInventorySortableRefinerValue(channelInventoryConfiguration, isInStockRefiner);

        const isInventoryRefinerValueExist = refiners.some(refiner => refiner.RefinerRecordId === inventoryRefinerValue?.RefinerRecordId);
        if (!isInventoryRefinerValueExist && inventoryRefinerValue) {
            refiners.push(inventoryRefinerValue);
        }
    }

    if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
        const isInStock = true;
        const inventoryInStockRefinerValue = getInventorySortableRefinerValue(channelInventoryConfiguration, isInStock);

        const isInventoryInStockRefinerValueExist = refiners.some(
            refiner => refiner.RefinerRecordId === inventoryInStockRefinerValue?.RefinerRecordId
        );
        if (!isInventoryInStockRefinerValueExist && inventoryInStockRefinerValue) {
            refiners.push(inventoryInStockRefinerValue);
        }
    }

    const alcoholFilter = shouldFilterAlcohol(context);
    const attributeRefiners = await getAllAttributeRefiners(context);

    // Either 'refiners' or 'defaultRefiners' will be used on 'isUpdateRefinerPanel'. But either way it should always
    // contain the zero price filter and alcohol refiner.
    refiners = addOrUpdateZeroPriceRefiner(context, refiners)
        .concat(alcoholFilter ? await getAlcoholRefiners(context) : [])
        .concat(attributeRefiners);
    const defaultRefiners = addOrUpdateZeroPriceRefiner(context)
        .concat(alcoholFilter ? await getAlcoholRefiners(context) : [])
        .concat(attributeRefiners);

    if (input.productScopeAttributeRecordId && input.productScopeAttributeValue) {
        const productScopeAttributeValues = input.productScopeAttributeValue
            .split('|')
            .map(value => value.trim())
            .filter(Boolean);
        const productScopeAttributeRecordId = Number(input.productScopeAttributeRecordId);

        if (Number.isNaN(productScopeAttributeRecordId)) {
            console.error('Failed to cast product scope attribute record id into a number. Ignoring it.');
        } else {
            for (const attributeValue of productScopeAttributeValues) {
                const productScopeRefiner: ProductRefinerValue = {
                    DataTypeValue: AttributeDataType.Text,
                    LeftValueBoundString: attributeValue.toString(),
                    RefinerRecordId: productScopeAttributeRecordId,
                    RefinerSourceValue: ProductRefinerSource.Attribute,
                    RightValueBoundString: attributeValue.toString(),
                    UnitText: ''
                };
                refiners.push(productScopeRefiner);
            }
        }
    }

    if (input.pageType === 'Category') {
        input.refiners = input.isUpdateRefinerPanel ? refiners : defaultRefiners;
        return getProductRefinerHierarchy(
            {
                CategoryIds: input.category ? [input.category || DefaultValues.defaultCategoryIdValue] : [],
                Context: {
                    ChannelId: input.apiSettings.channelId,
                    CatalogId: input.catalogId
                },
                Refinement: input.isUpdateRefinerPanel ? refiners : defaultRefiners,
                SearchCondition: input.searchText
            },
            input.queryResultSettings,
            context
        );
    } else {
        if (input.searchText && context.requestContext.query && context.requestContext.query.q && !context.requestContext.query.recommendation) {
            input.refiners = input.isUpdateRefinerPanel ? refiners : defaultRefiners;
            return getProductRefinerHierarchy(
                {
                    SearchCondition: input.searchText,
                    Context: {
                        ChannelId: input.apiSettings.channelId,
                        CatalogId: input.catalogId
                    },
                    Refinement: input.isUpdateRefinerPanel ? refiners : defaultRefiners
                },
                input.queryResultSettings,
                context
            );
        }
        if (input.searchText && context.requestContext.query && context.requestContext.query.recommendation) {
            const searchObject = JSON.parse(input.searchText);
            if (context.requestContext.query.productId) {
                searchProductId = Number(searchObject.ProductId);
            }
            if (Number.isNaN(searchProductId)) {
                throw new Error('Failed to cast search product id into a number.');
            } else if (!searchObject.Recommendation) {
                throw new Error('Failed to retrieve the Recommendation.');
            } else {
                const searchCriteriaInput: ProductSearchCriteria = {};
                input.refiners = input.isUpdateRefinerPanel ? refiners : defaultRefiners;
                searchCriteriaInput.Context = {
                    ChannelId: input.apiSettings.channelId,
                    CatalogId: input.catalogId
                };
                searchCriteriaInput.SearchCondition = searchObject.SearchText;
                searchCriteriaInput.Refinement = input.isUpdateRefinerPanel ? refiners : defaultRefiners;
                searchCriteriaInput.RecommendationListId = searchObject.Recommendation;
                if (searchProductId) {
                    searchCriteriaInput.Ids = [searchProductId || DefaultValues.defaultProductIdValue];
                }
                return getProductRefinerHierarchy(searchCriteriaInput, input.queryResultSettings, context);
            }
        } else {
            throw new Error('[GetFullProductsForCollection]Search Page Detected, but no q= or productId= query parameter found');
        }
    }
}

export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search-result-container/get-refiners-for-collection',
    action: <IAction<IProductRefinerHierarchy[]>>getRefinersForCollectionDataAction,
    input: createInput
});

export default actionDataAction;
